import { VERIFICATION_STATUES } from '@/constants';
import { cn } from '@/utils/cn';
import checkCircleIcon from '@iconify/icons-ant-design/check-circle-filled';
import { InlineIcon } from '@iconify/react/dist/offline';
import { ReactNode } from 'react';
import { OverlayTriggerProps } from 'rsuite/esm/internals/Overlay/OverlayTrigger';
import styled from 'styled-components';
import { RsuiteTooltipWrapper } from './tooltips';

const TOOLTIP_VERIFIED_LISTING = 'Verified listing';
const TOOLTIP_VERIFIED_PROPERTY = 'Verified property';
const TOOLTIP_PENDING_VERIFICATION = 'Pending verification';

const TooltipWrapperContainer = styled.div`
  display: inline-block;
`;

interface BalloonCSSTooltipWrapperProps {
  children: ReactNode;
  /** Text content for tooltip */
  text: string;
  /** Identifier for tooltip to link to triggering element */
  tooltipIdentifier: string;
  tooltipPosition: unknown;
}

function BalloonCSSTooltipWrapper({
  children,
  text,
  tooltipPosition,
}: BalloonCSSTooltipWrapperProps) {
  return (
    <TooltipWrapperContainer
      aria-label={text}
      data-balloon-pos={tooltipPosition}
    >
      {children}
    </TooltipWrapperContainer>
  );
}

const COLORS: Record<string, string> = {
  [VERIFICATION_STATUES.VERIFIED]: '#08979c',
  [VERIFICATION_STATUES.PENDING_VERIFICATION]: '#555659',
};

interface VerifiedIconProps {
  /** Verification status of the item (e.g., "verified", "unverified") */
  verifiedStatus: string;
  /** Additional CSS class names to apply to the icon container */
  className?: string;
  /** Indicates whether the verified item is a property rather than a listing or other doc type */
  isProperty?: boolean;
  tooltipPosition?: OverlayTriggerProps['placement'];
  /** Instructs whether to use RSuite or BalloonCSS for the tooltip */
  ssrFriendly?: boolean;
}

/**
 * Icon showing verification status of a property or listing
 */
export function VerifiedIcon(props: VerifiedIconProps) {
  const {
    verifiedStatus,
    className,
    isProperty,
    tooltipPosition,
    ssrFriendly,
  } = props;

  const tooltipIdentifier = 'verified-icon-tooltip';

  const WrapperComponent = ssrFriendly
    ? BalloonCSSTooltipWrapper
    : RsuiteTooltipWrapper;

  const tooltip =
    // eslint-disable-next-line no-nested-ternary
    verifiedStatus === VERIFICATION_STATUES.VERIFIED
      ? isProperty
        ? TOOLTIP_VERIFIED_PROPERTY
        : TOOLTIP_VERIFIED_LISTING
      : TOOLTIP_PENDING_VERIFICATION;

  if (!verifiedStatus || verifiedStatus === VERIFICATION_STATUES.UNVERIFIED)
    return null;

  return (
    <WrapperComponent
      text={tooltip}
      tooltipIdentifier={tooltipIdentifier}
      tooltipPosition={tooltipPosition}
    >
      <InlineIcon
        aria-describedby={tooltipIdentifier}
        aria-hidden={false} // Default behavior is hidden
        aria-label={tooltip} // Match with the tooltip text
        icon={checkCircleIcon}
        color={COLORS[verifiedStatus]}
        className={cn('rs-icon', className)}
      />
    </WrapperComponent>
  );
}
